// One lap of Marina Bay, drawn from the driver's seat.
//
// A classic pseudo-3D road: the track is a list of flat segments, each one
// projected onto the screen from a camera that sits where the driver's eyes
// are. Bends are faked by sliding each segment sideways by the running total
// of the curves before it, which is cheap enough for any phone and reads as a
// real road at speed. The cockpit (pillars, dash and wheel) is drawn over the
// top every frame, so the player is always "inside the car".

import { buildTrack, nextCorner, SEGMENT_LENGTH as SEG } from './track.js';

const ROAD_WIDTH = 2000;          // half the road, in world units
const CAMERA_HEIGHT = 900;
const FOV = 100;
const CAMERA_DEPTH = 1 / Math.tan(((FOV / 2) * Math.PI) / 180);
const DRAW_DISTANCE = 260;        // segments
const MAX_SPEED = SEG * 43;       // world units per second (shown as 300 km/h)
const ACCEL = MAX_SPEED / 5;
const BRAKE = MAX_SPEED * 0.9;
const COAST = MAX_SPEED / 6;
const OFF_ROAD_LIMIT = MAX_SPEED * 0.38;
const CENTRIFUGAL = 0.2;
const WALL = 1.45;                // where the barriers stop the car
const START_SEGMENT = 12;         // the start/finish line
const RIVAL_COLOURS = ['#1f6feb', '#f2c200', '#2e9d4f', '#f0f0f0', '#7a3fd0'];
const RIVAL_PACE = [0.9, 0.87, 0.85, 0.83, 0.81];

export const KMH_AT_MAX = 300;

const wrap = (v, max) => ((v % max) + max) % max;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export function formatLapTime(ms) {
  if (!Number.isFinite(ms) || ms <= 0) return '0:00.0';
  const total = Math.floor(ms / 100);         // tenths
  const m = Math.floor(total / 600);
  const s = Math.floor((total % 600) / 10);
  const t = total % 10;
  return `${m}:${String(s).padStart(2, '0')}.${t}`;
}

export class Race {
  /**
   * @param {HTMLCanvasElement} canvas
   * @param {{ onFinish?: (r: {timeMs:number, position:number}) => void,
   *           onTick?: (s: object) => void, reducedMotion?: boolean }} opts
   */
  constructor(canvas, opts = {}) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.opts = opts;
    this.segments = buildTrack();
    this.trackLength = this.segments.length * SEG;
    this.playerZ = CAMERA_HEIGHT * CAMERA_DEPTH;

    // The player starts at the back of a six-car grid, just behind the line.
    this.position = START_SEGMENT * SEG - this.playerZ - SEG * 2;
    this.startZ = this.position;
    this.playerX = 0.42;
    this.speed = 0;
    this.steer = 0;          // -1 … 1, what the controls ask for
    this.wheel = 0;          // the drawn wheel, eased towards `steer`
    this.braking = false;
    this.phase = 'lights';   // lights → race → finished
    this.clock = 0;
    this.lightsOutAt = 4 + Math.random() * 0.8;
    this.raceTime = 0;
    this.wallCooldown = 0;
    this.skyOffset = 0;
    this.shake = 0;

    const playerWorld = this.position + this.playerZ;
    this.rivals = RIVAL_COLOURS.map((colour, k) => ({
      colour,
      number: [7, 18, 26, 55, 88][k],
      x: k % 2 === 0 ? -0.42 : 0.42,
      z: playerWorld + (5 - k) * SEG * 3,
      speed: 0,
      pace: RIVAL_PACE[k] * MAX_SPEED,
    }));

    this.resize();
    this._raf = 0;
    this._last = 0;
    this._loop = this._loop.bind(this);
  }

  start() {
    this._last = window.performance.now();
    this._raf = requestAnimationFrame(this._loop);
  }

  stop() {
    cancelAnimationFrame(this._raf);
    this._raf = 0;
  }

  setSteer(v) { this.steer = clamp(v, -1, 1); }
  setBrake(on) { this.braking = !!on; }

  resize() {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = this.canvas.clientWidth || 360;
    const h = this.canvas.clientHeight || 640;
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.w = w;
    this.h = h;
    this.horizon = Math.round(h * 0.44);
    this._skyline = drawSkyline(w, this.horizon);
  }

  /** Where the race stands, for the dashboard. */
  standing() {
    const mine = this._progress();
    const ahead = this.rivals.filter((r) => r.z - (this.startZ + this.playerZ) > mine).length;
    const seg = this._segmentAt(this.position + this.playerZ);
    return {
      phase: this.phase,
      lights: this.phase === 'lights' ? Math.min(5, Math.floor(this.clock / 0.75)) : 0,
      timeMs: this.raceTime * 1000,
      position: ahead + 1,
      cars: this.rivals.length + 1,
      kmh: Math.round((this.speed / MAX_SPEED) * KMH_AT_MAX),
      corner: nextCorner(this.segments, seg.index),
      lapShare: clamp(mine / this.trackLength, 0, 1),
      offTrack: Math.abs(this.playerX) > 1,
    };
  }

  // ── simulation ──────────────────────────────────────────────────────────

  _progress() { return this.position - this.startZ; }

  _segmentAt(z) {
    return this.segments[Math.floor(wrap(z, this.trackLength) / SEG) % this.segments.length];
  }

  _update(dt) {
    this.clock += dt;
    this.wheel += (this.steer - this.wheel) * Math.min(1, dt * 10);
    if (this.phase === 'lights') {
      if (this.clock >= this.lightsOutAt) this.phase = 'race';
      return;
    }
    if (this.phase === 'race') this.raceTime += dt;

    const seg = this._segmentAt(this.position + this.playerZ);
    const pct = this.speed / MAX_SPEED;
    const before = this.position;

    // Throttle is automatic: the player steers, and brakes when they choose.
    if (this.phase === 'finished') this.speed = Math.max(0, this.speed - COAST * 2 * dt);
    else if (this.braking) this.speed -= BRAKE * dt;
    else this.speed += ACCEL * dt;

    const steerRate = dt * 2.5 * Math.max(0.4, pct);
    this.playerX += this.steer * steerRate;
    this.playerX -= steerRate * pct * seg.curve * CENTRIFUGAL;

    if (Math.abs(this.playerX) > 1 && this.speed > OFF_ROAD_LIMIT) {
      this.speed -= MAX_SPEED * 1.1 * dt;          // the run-off slows you right down
    }
    this.wallCooldown -= dt;
    if (Math.abs(this.playerX) > WALL) {
      this.playerX = Math.sign(this.playerX) * WALL;
      if (this.wallCooldown <= 0) {
        this.speed *= 0.6;
        this.shake = 0.35;
        this.wallCooldown = 0.5;
      }
    }
    this.speed = clamp(this.speed, 0, MAX_SPEED);
    this.position += this.speed * dt;

    // Rivals hold their line and lift for the bends.
    for (const r of this.rivals) {
      const rs = this._segmentAt(r.z);
      const target = this.phase === 'race' || this.phase === 'finished'
        ? r.pace * (1 - Math.min(0.3, Math.abs(rs.curve) * 0.045))
        : 0;
      r.speed += clamp(target - r.speed, -BRAKE * dt, ACCEL * 0.9 * dt);
      r.z += r.speed * dt;
    }

    // Run into the back of a rival and you tuck in behind them.
    if (this.phase === 'race') {
      const me = this.position + this.playerZ;
      for (const r of this.rivals) {
        const gap = r.z - me;
        if (gap > 0 && gap < SEG * 1.4 && Math.abs(r.x - this.playerX) < 0.42 && this.speed > r.speed) {
          this.speed = r.speed * 0.9;
          this.position = r.z - SEG * 1.4 - this.playerZ;
          this.shake = 0.25;
        }
      }
    }

    this.skyOffset = wrap(this.skyOffset + 0.0009 * seg.curve * ((this.position - before) / SEG), 1);
    this.shake = Math.max(0, this.shake - dt);

    if (this.phase === 'race' && this._progress() >= this.trackLength) {
      this.phase = 'finished';
      const result = { timeMs: Math.round(this.raceTime * 1000), position: this.standing().position };
      this.result = result;
      this.opts.onFinish?.(result);
    }
  }

  // ── drawing ─────────────────────────────────────────────────────────────

  _project(p, camX, camY, camZ) {
    const cz = p.z - camZ;
    const scale = CAMERA_DEPTH / cz;
    return {
      cz,
      scale,
      x: Math.round(this.w / 2 + scale * (p.x - camX) * this.w / 2),
      y: Math.round(this.horizon - scale * (p.y - camY) * this.w / 2),
      w: Math.round(scale * ROAD_WIDTH * this.w / 2),
    };
  }

  _render() {
    const { ctx, w, h } = this;
    const motion = !this.opts.reducedMotion;
    ctx.save();
    if (motion && this.shake > 0) ctx.translate((Math.random() - 0.5) * 6, (Math.random() - 0.5) * 4);
    const speedJitter = motion ? (this.speed / MAX_SPEED) * 0.8 * Math.sin(this.clock * 40) : 0;

    // Sky and the Singapore skyline at night.
    const sky = ctx.createLinearGradient(0, 0, 0, this.horizon);
    sky.addColorStop(0, '#050817');
    sky.addColorStop(1, '#1d2752');
    ctx.fillStyle = sky;
    ctx.fillRect(-10, -10, w + 20, this.horizon + 12);
    const sk = this._skyline;
    const off = Math.round(this.skyOffset * sk.width);
    ctx.drawImage(sk, off, 0, Math.min(w, sk.width - off), sk.height, 0, this.horizon - sk.height, Math.min(w, sk.width - off), sk.height);
    if (sk.width - off < w) ctx.drawImage(sk, 0, 0, w - (sk.width - off), sk.height, sk.width - off, this.horizon - sk.height, w - (sk.width - off), sk.height);
    ctx.fillStyle = '#101218';
    ctx.fillRect(-10, this.horizon, w + 20, h - this.horizon + 10);

    // Project every visible segment, nearest first …
    const base = this._segmentAt(this.position);
    const basePct = wrap(this.position, SEG) / SEG;
    const camY = CAMERA_HEIGHT + speedJitter * 6;
    let x = 0;
    let dx = -(base.curve * basePct);
    let maxy = h;
    const drawn = [];
    for (let n = 0; n < DRAW_DISTANCE; n++) {
      const s = this.segments[(base.index + n) % this.segments.length];
      const looped = s.index < base.index;
      const camZ = this.position - (looped ? this.trackLength : 0);
      const camX = this.playerX * ROAD_WIDTH;
      const p1 = this._project({ x: 0, y: 0, z: s.index * SEG }, camX - x, camY, camZ);
      const p2 = this._project({ x: 0, y: 0, z: (s.index + 1) * SEG }, camX - x - dx, camY, camZ);
      x += dx;
      dx += s.curve;
      if (p1.cz <= CAMERA_DEPTH || p2.y >= p1.y || p2.y >= maxy) continue;
      drawn.push({ s, p1, p2, n });
      maxy = p1.y;
    }

    // … then paint them furthest first, so near walls cover far road.
    const carsBySeg = new Map();
    for (const r of this.rivals) {
      const idx = this._segmentAt(r.z).index;
      if (!carsBySeg.has(idx)) carsBySeg.set(idx, []);
      carsBySeg.get(idx).push(r);
    }
    for (let i = drawn.length - 1; i >= 0; i--) {
      const { s, p1, p2 } = drawn[i];
      this._drawSegment(s, p1, p2);
      const cars = carsBySeg.get(s.index);
      if (cars) for (const r of cars) this._drawRival(r, s, p1, p2);
    }

    ctx.restore();
    this._drawCockpit();
  }

  _quad(x1, y1, w1, x2, y2, w2, colour) {
    const { ctx } = this;
    ctx.fillStyle = colour;
    ctx.beginPath();
    ctx.moveTo(x1 - w1, y1);
    ctx.lineTo(x2 - w2, y2);
    ctx.lineTo(x2 + w2, y2);
    ctx.lineTo(x1 + w1, y1);
    ctx.closePath();
    ctx.fill();
  }

  _drawSegment(s, p1, p2) {
    const { ctx } = this;
    const band = Math.floor(s.index / 3) % 2;
    const isLine = s.index === START_SEGMENT || s.index === START_SEGMENT + 1;

    // Run-off and the street beyond.
    ctx.fillStyle = band ? '#1a1d24' : '#16181f';
    ctx.fillRect(0, p2.y, this.w, p1.y - p2.y + 1);
    this._quad(p1.x, p1.y, p1.w * 1.4, p2.x, p2.y, p2.w * 1.4, band ? '#2c3a2c' : '#273427');

    // Kerbs, road, edge lines.
    this._quad(p1.x, p1.y, p1.w * 1.12, p2.x, p2.y, p2.w * 1.12, band ? '#d8262b' : '#f3f3f3');
    this._quad(p1.x, p1.y, p1.w, p2.x, p2.y, p2.w, band ? '#41434b' : '#3c3e46');
    if (isLine) {
      const cells = 12;
      for (let c = 0; c < cells; c++) {
        const f1 = -1 + (2 * c) / cells;
        const f2 = -1 + (2 * (c + 1)) / cells;
        ctx.fillStyle = (c + s.index) % 2 ? '#111' : '#fafafa';
        ctx.beginPath();
        ctx.moveTo(p1.x + p1.w * f1, p1.y);
        ctx.lineTo(p2.x + p2.w * f1, p2.y);
        ctx.lineTo(p2.x + p2.w * f2, p2.y);
        ctx.lineTo(p1.x + p1.w * f2, p1.y);
        ctx.fill();
      }
    } else {
      const lw1 = Math.max(1, p1.w / 40);
      const lw2 = Math.max(1, p2.w / 40);
      ctx.fillStyle = '#e8e8e8';
      for (const side of [-1, 1]) {
        ctx.beginPath();
        ctx.moveTo(p1.x + side * (p1.w - lw1), p1.y);
        ctx.lineTo(p2.x + side * (p2.w - lw2), p2.y);
        ctx.lineTo(p2.x + side * p2.w, p2.y);
        ctx.lineTo(p1.x + side * p1.w, p1.y);
        ctx.fill();
      }
    }

    // Concrete walls and catch fence, lit by the floodlights.
    const wallH1 = p1.scale * 700 * this.w / 2;
    const wallH2 = p2.scale * 700 * this.w / 2;
    for (const side of [-1, 1]) {
      const x1 = p1.x + side * p1.w * 1.55;
      const x2 = p2.x + side * p2.w * 1.55;
      ctx.fillStyle = band ? '#d9dde6' : '#1d4fa8';
      ctx.beginPath();
      ctx.moveTo(x1, p1.y);
      ctx.lineTo(x2, p2.y);
      ctx.lineTo(x2, p2.y - wallH2);
      ctx.lineTo(x1, p1.y - wallH1);
      ctx.fill();
      ctx.fillStyle = 'rgba(160,170,190,0.18)';
      ctx.beginPath();
      ctx.moveTo(x1, p1.y - wallH1);
      ctx.lineTo(x2, p2.y - wallH2);
      ctx.lineTo(x2, p2.y - wallH2 * 3.2);
      ctx.lineTo(x1, p1.y - wallH1 * 3.2);
      ctx.fill();
    }

    // A floodlight tower every few segments.
    if (s.index % 10 === 0) {
      for (const side of [-1, 1]) {
        const px = p1.x + side * p1.w * 1.75;
        const top = p1.y - p1.scale * 5200 * this.w / 2;
        const pw = Math.max(1, p1.scale * 70 * this.w / 2);
        ctx.fillStyle = '#5b6170';
        ctx.fillRect(px - pw / 2, top, pw, p1.y - top);
        const lw = Math.max(3, p1.scale * 700 * this.w / 2);
        ctx.fillStyle = 'rgba(255,248,214,0.12)';
        ctx.beginPath();
        ctx.arc(px, top, Math.min(lw * 1.1, 40), 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#fff6cf';
        ctx.fillRect(px - lw / 2, top - lw / 4, lw, lw / 2);
      }
    }
  }

  _drawRival(r, s, p1, p2) {
    const { ctx } = this;
    const pct = wrap(r.z, SEG) / SEG;
    const scale = p1.scale + (p2.scale - p1.scale) * pct;
    const cx = p1.x + (p2.x - p1.x) * pct + scale * r.x * ROAD_WIDTH * this.w / 2;
    const cy = p1.y + (p2.y - p1.y) * pct;
    const cw = scale * 820 * this.w / 2;     // car width on screen
    if (cw < 2) return;
    const ch = cw * 0.42;
    const top = cy - ch;

    ctx.fillStyle = 'rgba(0,0,0,0.35)';
    ctx.fillRect(cx - cw * 0.55, cy - ch * 0.08, cw * 1.1, ch * 0.16);
    ctx.fillStyle = '#111';
    ctx.fillRect(cx - cw * 0.52, cy - ch * 0.42, cw * 0.2, ch * 0.42);
    ctx.fillRect(cx + cw * 0.32, cy - ch * 0.42, cw * 0.2, ch * 0.42);
    ctx.fillStyle = r.colour;
    ctx.beginPath();
    ctx.moveTo(cx - cw * 0.48, cy - ch * 0.2);
    ctx.lineTo(cx - cw * 0.44, top + ch * 0.3);
    ctx.lineTo(cx - cw * 0.26, top + ch * 0.05);
    ctx.lineTo(cx + cw * 0.26, top + ch * 0.05);
    ctx.lineTo(cx + cw * 0.44, top + ch * 0.3);
    ctx.lineTo(cx + cw * 0.48, cy - ch * 0.2);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#0b0d12';
    ctx.fillRect(cx - cw * 0.22, top + ch * 0.12, cw * 0.44, ch * 0.22);
    ctx.fillStyle = '#ff2a2a';
    ctx.fillRect(cx - cw * 0.44, cy - ch * 0.42, cw * 0.16, ch * 0.1);
    ctx.fillRect(cx + cw * 0.28, cy - ch * 0.42, cw * 0.16, ch * 0.1);
    ctx.fillStyle = '#16181d';
    ctx.fillRect(cx - cw * 0.5, top - ch * 0.12, cw, ch * 0.1);       // rear wing
    ctx.fillRect(cx - cw * 0.3, top - ch * 0.04, cw * 0.05, ch * 0.12);
    ctx.fillRect(cx + cw * 0.25, top - ch * 0.04, cw * 0.05, ch * 0.12);
    if (cw > 40) {
      ctx.fillStyle = r.colour === '#f0f0f0' ? '#111' : '#fff';
      ctx.font = `700 ${Math.round(ch * 0.2)}px system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(String(r.number), cx, cy - ch * 0.5);
    }
  }

  _drawCockpit() {
    const { ctx, w, h } = this;
    const dashTop = h * 0.74;

    // Roll hoop and pillars.
    ctx.fillStyle = '#07080b';
    ctx.fillRect(0, 0, w, h * 0.05);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(w * 0.1, 0);
    ctx.lineTo(w * 0.02, dashTop);
    ctx.lineTo(0, dashTop);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(w, 0);
    ctx.lineTo(w * 0.9, 0);
    ctx.lineTo(w * 0.98, dashTop);
    ctx.lineTo(w, dashTop);
    ctx.fill();
    // Rear-view mirror.
    ctx.fillStyle = '#0d0f14';
    ctx.fillRect(w * 0.4, h * 0.05, w * 0.2, h * 0.035);
    ctx.fillStyle = '#25304f';
    ctx.fillRect(w * 0.41, h * 0.056, w * 0.18, h * 0.023);

    // Red bonnet line, then the dash.
    ctx.fillStyle = '#b3121a';
    ctx.beginPath();
    ctx.moveTo(0, dashTop + 4);
    ctx.quadraticCurveTo(w / 2, dashTop - h * 0.05, w, dashTop + 4);
    ctx.lineTo(w, h);
    ctx.lineTo(0, h);
    ctx.fill();
    ctx.fillStyle = '#14161b';
    ctx.beginPath();
    ctx.moveTo(0, dashTop + h * 0.03);
    ctx.quadraticCurveTo(w / 2, dashTop - h * 0.02, w, dashTop + h * 0.03);
    ctx.lineTo(w, h);
    ctx.lineTo(0, h);
    ctx.fill();
    ctx.strokeStyle = '#c0262d';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(0, dashTop + h * 0.045);
    ctx.quadraticCurveTo(w / 2, dashTop - h * 0.005, w, dashTop + h * 0.045);
    ctx.stroke();

    // The wheel turns with the steering.
    const r = Math.min(w * 0.3, h * 0.2);
    const cx = w / 2;
    const cy = h - r * 0.35;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(this.wheel * 1.4);
    ctx.lineWidth = r * 0.16;
    ctx.strokeStyle = '#1e2026';
    ctx.beginPath();
    ctx.arc(0, 0, r, Math.PI * 0.95, Math.PI * 2.05);
    ctx.stroke();
    ctx.fillStyle = '#1e2026';
    ctx.fillRect(-r * 1.05, -r * 0.1, r * 0.35, r * 0.5);
    ctx.fillRect(r * 0.7, -r * 0.1, r * 0.35, r * 0.5);
    ctx.fillStyle = '#26282f';
    roundRect(ctx, -r * 0.55, -r * 0.35, r * 1.1, r * 0.75, r * 0.12);
    ctx.fill();
    ctx.fillStyle = '#0a2a1e';
    roundRect(ctx, -r * 0.38, -r * 0.27, r * 0.76, r * 0.36, r * 0.05);
    ctx.fill();
    const kmh = Math.round((this.speed / MAX_SPEED) * KMH_AT_MAX);
    ctx.fillStyle = '#5dffb0';
    ctx.textAlign = 'center';
    ctx.font = `700 ${Math.round(r * 0.22)}px ui-monospace, Menlo, monospace`;
    ctx.fillText(String(kmh), 0, -r * 0.07);
    ctx.font = `600 ${Math.round(r * 0.09)}px system-ui, sans-serif`;
    ctx.fillText('KM/H', 0, r * 0.05);
    // Shift lights across the top of the wheel.
    const lit = Math.round((this.speed / MAX_SPEED) * 8);
    for (let i = 0; i < 8; i++) {
      ctx.fillStyle = i < lit ? (i < 4 ? '#3ddc84' : i < 6 ? '#ffcc00' : '#ff3b30') : '#333640';
      ctx.beginPath();
      ctx.arc(-r * 0.35 + (i * r * 0.7) / 7, -r * 0.31, r * 0.025, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  _loop(now) {
    const dt = Math.min(0.05, (now - this._last) / 1000);
    this._last = now;
    this._update(dt);
    this._render();
    this.opts.onTick?.(this.standing());
    this._raf = requestAnimationFrame(this._loop);
  }
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// The skyline, drawn once per size: towers with lit windows, the three
// Marina Bay Sands towers with the SkyPark across them, and the Flyer.
function drawSkyline(w, horizon) {
  const width = Math.max(1, Math.round(w * 3));
  const height = Math.max(1, Math.round(horizon * 0.55));
  const c = document.createElement('canvas');
  c.width = width;
  c.height = height;
  const g = c.getContext('2d');
  let seed = 7;
  const rand = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);

  // Stars.
  g.fillStyle = 'rgba(255,255,255,0.5)';
  for (let i = 0; i < 70; i++) g.fillRect(rand() * width, rand() * height * 0.4, 1, 1);

  // Ordinary towers.
  let x = 0;
  while (x < width) {
    const bw = 18 + rand() * 46;
    const bh = height * (0.25 + rand() * 0.6);
    g.fillStyle = rand() > 0.5 ? '#0d1330' : '#111a3d';
    g.fillRect(x, height - bh, bw, bh);
    g.fillStyle = 'rgba(255,214,120,0.55)';
    for (let wy = height - bh + 5; wy < height - 4; wy += 7) {
      for (let wx = x + 3; wx < x + bw - 3; wx += 6) if (rand() > 0.55) g.fillRect(wx, wy, 2, 3);
    }
    x += bw + rand() * 6;
  }

  // Marina Bay Sands.
  const mx = width * 0.32;
  const mh = height * 0.92;
  const tw = Math.max(14, w * 0.035);
  g.fillStyle = '#18224a';
  for (let i = 0; i < 3; i++) {
    const tx = mx + i * tw * 1.7;
    g.beginPath();
    g.moveTo(tx, height);
    g.lineTo(tx + tw * 0.15, height - mh);
    g.lineTo(tx + tw, height - mh);
    g.lineTo(tx + tw * 0.9, height);
    g.fill();
  }
  g.fillStyle = '#22306a';
  g.fillRect(mx - tw * 0.4, height - mh - tw * 0.35, tw * 5.6, tw * 0.35);
  g.fillStyle = 'rgba(120,200,255,0.8)';
  g.fillRect(mx - tw * 0.4, height - mh - tw * 0.05, tw * 5.6, 2);

  // The Singapore Flyer.
  const fx = width * 0.62;
  const fr = height * 0.36;
  const fy = height - fr - height * 0.08;
  g.strokeStyle = 'rgba(160,220,255,0.75)';
  g.lineWidth = 2;
  g.beginPath();
  g.arc(fx, fy, fr, 0, Math.PI * 2);
  g.stroke();
  g.lineWidth = 1;
  for (let i = 0; i < 16; i++) {
    const a = (i / 16) * Math.PI * 2;
    g.beginPath();
    g.moveTo(fx, fy);
    g.lineTo(fx + Math.cos(a) * fr, fy + Math.sin(a) * fr);
    g.stroke();
  }
  g.strokeStyle = '#18224a';
  g.lineWidth = 4;
  g.beginPath();
  g.moveTo(fx, fy);
  g.lineTo(fx - fr * 0.45, height);
  g.moveTo(fx, fy);
  g.lineTo(fx + fr * 0.45, height);
  g.stroke();
  return c;
}
